/**
 * Find the URL of the hero section's image. Agent-generated pages mark
 * the hero with `data-section-id="hero"` (alias: `hero-section`); within
 * the hero, the visual is either:
 *   1. An <img> with a real src (most common for photo heroes)
 *   2. A computed CSS `background-image: url(...)` on the hero itself
 *      or one of its descendants
 *
 * Returns the resolved absolute URL or null when no hero image is found.
 * The intended use is "skip the page rasteriser entirely, use the hero
 * photo as the OG image directly" — which is what users want for share
 * previews: just the photo, no nav bar, no website text.
 */
export function extractHeroImageUrl(doc: Document): string | null {
  const heroSelectors = [
    '[data-section-id="hero" i]',
    '[data-section-id="hero-section" i]',
    'header[role="banner"]',
    'header.hero',
    'section.hero',
    '[class*="hero" i]',
  ];
  let hero: Element | null = null;
  for (const sel of heroSelectors) {
    try {
      hero = doc.querySelector(sel);
    } catch {
      // Some selectors are author-malformed; ignore.
    }
    if (hero) break;
  }
  if (!hero) return null;

  // Find candidate <img> elements inside the hero.
  //
  // Pick the LARGEST by intrinsic (natural) area, not the first in
  // document order. Hero sections frequently start with a small
  // logo / wordmark <img> followed by the actual hero photo —
  // grabbing the first match meant we used the logo (which usually
  // has the site name baked in as text), and that's the "header
  // text on OG" symptom users were seeing.
  //
  // We also exclude:
  //   - data: URIs (inline base64, often icons / spacers)
  //   - .svg (vector — usually logos / decorative glyphs, not hero
  //     photography; many SVG logos contain text)
  //   - <img>s nested inside <nav> / [role="navigation"] (those are
  //     navigation icons, never the hero subject)
  const imgs = Array.from(hero.querySelectorAll('img')).filter((img) => {
    const src = (img as HTMLImageElement).src;
    if (!src) return false;
    if (src.startsWith('data:')) return false;
    if (/\.svg(\?|$)/i.test(src)) return false;
    if (img.closest('nav, [role="navigation"]')) return false;
    return true;
  }) as HTMLImageElement[];

  if (imgs.length > 0) {
    const byAreaDesc = imgs.slice().sort((a, b) => {
      const aArea = (a.naturalWidth || 0) * (a.naturalHeight || 0);
      const bArea = (b.naturalWidth || 0) * (b.naturalHeight || 0);
      return bArea - aArea;
    });
    const winner = byAreaDesc[0];
    // 80_000 px² ≈ a 400×200 image — logos are usually <= 200×60.
    // If even the LARGEST candidate is below this we treat the whole
    // hero as not-having-a-photo and fall through to the
    // background-image search rather than serving up a logo as the
    // OG. This is the "do not serve a logo with text on it" guard.
    const winnerArea = (winner.naturalWidth || 0) * (winner.naturalHeight || 0);
    if (winnerArea >= 80_000) {
      return winner.src;
    }
  }

  // Fall back to background-image CSS — read computed style, since the
  // author may set it via class rule, not inline.
  const view = doc.defaultView;
  if (view) {
    const candidates: Element[] = [hero, ...Array.from(hero.querySelectorAll('*'))];
    for (const el of candidates) {
      const bg = view.getComputedStyle(el).backgroundImage;
      if (bg && bg !== 'none') {
        const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
        if (match) {
          const url = match[1];
          if (url && !url.startsWith('data:') && !/\.svg(\?|$)/i.test(url)) {
            return url;
          }
        }
      }
    }
  }
  return null;
}

/**
 * Fetch an image URL and cover-fit center-crop it onto a 1200×630 OG
 * canvas, returning a PNG blob. Throws on fetch error or canvas taint
 * (cross-origin without CORS headers).
 *
 * Cover-fit: the source image fills the entire canvas, scaled so the
 * shorter side matches and excess on the longer axis is cropped equally
 * top/bottom or left/right. Matches how iMessage / Slack render OG
 * images, so the hero subject stays centred.
 */
export async function fetchAndCropToOg(url: string): Promise<Blob> {
  // mode:'cors' is the default for cross-origin fetches; explicit here
  // so CORS-blocked CDNs throw instead of returning an opaque response
  // that we couldn't read into a Blob anyway.
  const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!response.ok) throw new Error(`fetch hero image: ${response.status}`);
  const sourceBlob = await response.blob();

  const objectUrl = URL.createObjectURL(sourceBlob);
  try {
    const img = new Image();
    // Setting crossOrigin lets canvas.toBlob succeed on CORS-served images.
    img.crossOrigin = 'anonymous';
    img.src = objectUrl;
    await img.decode();

    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas-2d-unavailable');

    // White background under the image — covers any transparency in PNGs
    // and any letterboxing if the image happens to be exactly the same
    // aspect (unlikely but cheap to be safe).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 1200, 630);

    const scale = Math.max(1200 / img.width, 630 / img.height);
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const dx = (1200 - drawW) / 2;
    const dy = (630 - drawH) / 2;
    ctx.drawImage(img, dx, dy, drawW, drawH);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas-toblob-failed'))),
        'image/png',
      );
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
